import {
  XMarkIcon,
  CalendarIcon,
  MapPinIcon,
  TagIcon,
} from '@heroicons/react/24/outline';

interface PostPreviewProps {
  title: string;
  content: string;
  excerpt: string;
  featured_image: string;
  featured_image_alt: string;
  images: Array<{ url: string; alt: string; caption: string }>;
  category_name?: string;
  tag_names: string[];
  location_name: string;
  location_country: string;
  onClose: () => void;
}

const PostPreview = ({
  title,
  content,
  excerpt,
  featured_image,
  featured_image_alt,
  images,
  category_name,
  tag_names,
  location_name,
  location_country,
  onClose,
}: PostPreviewProps) => {
  const formattedDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-white">
      {/* Sticky close bar */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
              Preview
            </span>
            <span className="text-sm text-gray-500">
              This is how your post will look when published
            </span>
          </div>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
            Close Preview
          </button>
        </div>
      </div>

      {/* Preview content — mirrors PostPage layout */}
      <article className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6 leading-tight">
              {title || 'Untitled Post'}
            </h1>

            {/* Meta info */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600 mb-6">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                <span>{formattedDate}</span>
              </div>

              {location_country && (
                <div className="flex items-center gap-2">
                  <MapPinIcon className="w-4 h-4" />
                  <span>
                    {location_name && `${location_name}, `}
                    {location_country}
                  </span>
                </div>
              )}

              {category_name && (
                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                  {category_name}
                </div>
              )}
            </div>

            {/* Tags */}
            {tag_names.length > 0 && (
              <div className="flex items-center justify-center gap-2 mb-8">
                <TagIcon className="w-4 h-4 text-gray-500" />
                <div className="flex flex-wrap gap-2">
                  {tag_names.map((name, i) => (
                    <span
                      key={i}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Featured Image */}
          {featured_image && (
            <div className="mb-8">
              <img
                src={featured_image}
                alt={featured_image_alt || title}
                className="w-full h-auto object-contain rounded-lg shadow-lg max-h-screen"
              />
            </div>
          )}

          {/* Excerpt */}
          {excerpt && (
            <div className="mb-8">
              <p className="text-xl text-gray-700 italic text-center leading-relaxed">
                {excerpt}
              </p>
            </div>
          )}

          {/* Content */}
          {content ? (
            <div className="prose prose-lg max-w-none">
              <div
                className="text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <p className="text-lg">No content yet</p>
              <p className="text-sm mt-1">
                Start writing to see a preview here
              </p>
            </div>
          )}

          {/* Image Gallery */}
          {images.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-display font-bold mb-6">
                Photo Gallery
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {images.map((image, index) => (
                  <figure key={index} className="group">
                    <img
                      src={image.url}
                      alt={image.alt || `Photo ${index + 1}`}
                      className="w-full h-64 object-cover rounded-lg shadow-md"
                    />
                    {image.caption && (
                      <figcaption className="mt-2 text-sm text-gray-600 italic text-center">
                        {image.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
};

export default PostPreview;
